import { faChevronRight, faSpinner, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReactNode, useContext, useState } from 'react';

import { Color } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import {
  useCacheInspect,
  useCacheScan,
  useDeleteCacheKey,
  useKnownCacheKeys
} from '@/hooks/useCacheConsole';
import { KeyInspect, KeyPreview, KeySummary, ScanResultEntry } from '@/types/cacheConsole';
import Badge from '@/ui/components/Badge';
import Button from '@/ui/components/Button';
import CopyContent from '@/ui/components/CopyContent';
import { formatBytes } from '@/utils/formatBytes';

import { ErrorDetail, isNotFound, Loading, PanelCard, PanelError } from '../../../shared';

const DEFAULT_PATTERN = '*';

const TTL_LABELS: Record<number, string> = { [-2]: 'gone', [-1]: 'no expiry' };

const formatTTL = (seconds: number): string => {
  const label = TTL_LABELS[seconds];
  if (label) return label;
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  if (seconds < 86400)
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  return `${Math.floor(seconds / 86400)}d ${Math.floor((seconds % 86400) / 3600)}h`;
};

const TYPE_COLOR: Record<string, Color> = {
  string: Color.BLUE,
  list: Color.INDIGO,
  hash: Color.GREEN,
  set: Color.AMBER,
  zset: Color.ORANGE,
  stream: Color.TEAL
};

const TypeBadge = ({ type }: { type: string }) => (
  <Badge color={TYPE_COLOR[type] ?? Color.GRAY}>{type}</Badge>
);

// ─── Preview renderers ─────────────────────────────────────────────────────
const renderString = (value: unknown): ReactNode => (
  <pre className="text-xs whitespace-pre-wrap break-all bg-white border rounded p-2 max-h-96 overflow-y-auto">
    {String(value)}
  </pre>
);

const renderPairTable = (
  pairs: [unknown, unknown][],
  leftLabel: string,
  rightLabel: string
): ReactNode => (
  <div className="overflow-x-auto">
    <table className="min-w-full text-sm">
      <thead>
        <tr className="text-left text-gray-500">
          <th className="py-1">{leftLabel}</th>
          <th className="py-1">{rightLabel}</th>
        </tr>
      </thead>
      <tbody>
        {pairs.map(([left, right], index) => (
          <tr key={index} className="border-t align-top">
            <td className="py-1 pr-3 font-mono break-all">{String(left)}</td>
            <td className="py-1 font-mono break-all">{String(right)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const renderEntries = (entries: unknown[]): ReactNode => (
  <div className="overflow-x-auto">
    <table className="min-w-full text-sm">
      <tbody>
        {entries.map((entry, index) => (
          <tr key={index} className="border-t">
            <td className="py-1 text-gray-400 text-xs w-10 align-top">{index}</td>
            <td className="py-1 font-mono break-all">{String(entry)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const PREVIEW_RENDERERS: Record<string, (value: unknown) => ReactNode> = {
  string: renderString,
  list: (value) => renderEntries(value as unknown[]),
  set: (value) => renderEntries(value as unknown[]),
  hash: (value) => renderPairTable(value as [unknown, unknown][], 'field', 'value'),
  zset: (value) => renderPairTable(value as [unknown, unknown][], 'member', 'score'),
  stream: (value) =>
    renderPairTable(
      (value as [unknown, Record<string, unknown>][]).map(([id, fields]) => [
        id,
        JSON.stringify(fields)
      ]),
      'id',
      'fields'
    )
};

const PreviewBody = ({ preview }: { preview: KeyPreview }) => {
  const renderer = PREVIEW_RENDERERS[preview.type];
  if (!renderer) {
    return <p className="text-sm text-gray-500">No renderer for type {preview.type}.</p>;
  }
  return <>{renderer(preview.value)}</>;
};

// ─── Subcomponents ─────────────────────────────────────────────────────────
const QuickFilters = ({
  selectedPattern,
  onToggle
}: {
  selectedPattern: string;
  onToggle: (key: string) => void;
}) => {
  const { host } = useContext(AppContext);
  const knownKeys = useKnownCacheKeys();
  return (
    <div className="mb-3">
      <div className="mb-1 text-xs text-gray-500">
        Quick filters (
        <a
          href={`${host}/organization/config#page=Engineering`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline">
          configured here
        </a>
        )
      </div>
      {knownKeys.error ? (
        <ErrorDetail error={knownKeys.error} />
      ) : (
        <div className="flex flex-wrap gap-2">
          {(knownKeys.data ?? []).map((key) => {
            const isSelected = key === selectedPattern;
            return (
              <button
                key={key}
                type="button"
                aria-pressed={isSelected}
                onClick={() => onToggle(key)}
                className={`rounded-full border px-3 py-1 font-mono text-xs ${
                  isSelected
                    ? 'border-blue-600 bg-blue-600 text-white'
                    : 'border-gray-300 text-gray-800 hover:bg-gray-50'
                }`}>
                {key}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const SearchControls = ({
  pattern,
  onPatternChange,
  onSearch,
  cursor,
  nextCursor,
  onNext,
  isFetching
}: {
  pattern: string;
  onPatternChange: (value: string) => void;
  onSearch: () => void;
  cursor: number;
  nextCursor: number | null;
  onNext: () => void;
  isFetching: boolean;
}) => (
  <div className="mb-3 flex flex-wrap items-center gap-2">
    <input
      type="text"
      value={pattern}
      onChange={(event) => onPatternChange(event.target.value)}
      onKeyDown={(event) => event.key === 'Enter' && onSearch()}
      placeholder="Glob pattern (e.g. *google_sheet*, eng:task_status:*)"
      className="flex-1 min-w-75 rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono"
    />
    <Button color={Color.BLUE} onClick={onSearch} disabled={isFetching}>
      {isFetching ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Search'}
    </Button>
    <Button
      color={Color.GRAY}
      onClick={onNext}
      disabled={isFetching || nextCursor === null || nextCursor === 0}>
      Next <FontAwesomeIcon icon={faChevronRight} />
    </Button>
    <span className="text-xs text-gray-500 font-mono">
      cursor: {cursor} → {nextCursor ?? '—'}
    </span>
  </div>
);

const KeyList = ({
  keys,
  selectedKey,
  onSelect
}: {
  keys: ScanResultEntry[];
  selectedKey: string | null;
  onSelect: (key: string) => void;
}) => (
  <div className="overflow-x-auto">
    <table className="min-w-full text-sm">
      <thead>
        <tr className="text-left text-gray-500">
          <th className="py-2">Key</th>
          <th className="py-2 w-20">Type</th>
          <th className="py-2 w-32 text-right">TTL</th>
        </tr>
      </thead>
      <tbody>
        {keys.map((entry) => (
          <tr
            key={entry.key}
            className={`border-t cursor-pointer hover:bg-gray-50 ${
              entry.key === selectedKey ? 'bg-blue-50' : ''
            }`}
            onClick={() => onSelect(entry.key)}>
            <td className="py-2 font-mono break-all">{entry.key}</td>
            <td className="py-2">
              <TypeBadge type={entry.type} />
            </td>
            <td className="py-2 text-right whitespace-nowrap font-mono">
              {formatTTL(entry.ttl_seconds)}
            </td>
          </tr>
        ))}
        {keys.length === 0 && (
          <tr>
            <td colSpan={3} className="py-3 text-sm text-gray-500">
              No keys match. Try a wider pattern (or click Next — sparse matches may need several
              pages).
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

const InspectDetail = ({
  selectedKey,
  inspect,
  onDelete
}: {
  selectedKey: string;
  inspect: KeyInspect;
  onDelete: (key: string) => void;
}) => {
  const { summary, preview } = inspect;
  return (
    <PanelCard title={`Selected: ${selectedKey}`}>
      <SummaryLine summary={summary} />
      <PreviewSection preview={preview} />
      <div className="mt-4">
        <Button color={Color.RED} onClick={() => onDelete(selectedKey)}>
          <FontAwesomeIcon icon={faTrash} /> Delete key
        </Button>
      </div>
    </PanelCard>
  );
};

const SummaryLine = ({ summary }: { summary: KeySummary }) => (
  <div className="mb-3 flex flex-wrap items-center gap-3 text-xs text-gray-600">
    <span className="flex items-center gap-1">
      Type: <TypeBadge type={summary.type} />
    </span>
    <span>TTL: {formatTTL(summary.ttl_seconds)}</span>
    {summary.memory_bytes !== null && <span>Memory: {formatBytes(summary.memory_bytes)}</span>}
    {summary.encoding && (
      <span>
        Encoding: <span className="font-mono">{summary.encoding}</span>
      </span>
    )}
  </div>
);

const shownCount = (preview: KeyPreview): number =>
  preview.unit === 'bytes' ? (preview.value as string).length : (preview.value as unknown[]).length;

const previewCountLabel = (preview: KeyPreview): string => {
  const shown = shownCount(preview).toLocaleString();
  if (!preview.truncated) {
    return `${(preview.total_count ?? shownCount(preview)).toLocaleString()} ${preview.unit}.`;
  }
  const total = preview.total_count?.toLocaleString();
  return `Showing the first ${shown} of ${total} ${preview.unit} — server-truncated.`;
};

const PreviewSection = ({ preview }: { preview: KeyPreview | null }) => {
  if (!preview) return <p className="text-sm text-gray-500">No preview available.</p>;
  return (
    <>
      <div className="mb-2 text-xs text-gray-500">{previewCountLabel(preview)}</div>
      <PreviewBody preview={preview} />
      <div className="mt-2 text-xs">
        <CopyContent content={JSON.stringify(preview.value)} iconPosition="left">
          <span className="text-gray-500">Copy raw JSON</span>
        </CopyContent>
      </div>
    </>
  );
};

// ─── Main panel ─────────────────────────────────────────────────────────────
const CachePanel = () => {
  const [pattern, setPattern] = useState(DEFAULT_PATTERN);
  const [submittedPattern, setSubmittedPattern] = useState(DEFAULT_PATTERN);
  const [cursor, setCursor] = useState(0);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const scan = useCacheScan(submittedPattern, cursor);
  const inspect = useCacheInspect(selectedKey);
  const deleteMutation = useDeleteCacheKey();

  const runSearch = (nextPattern: string) => {
    setPattern(nextPattern);
    setSubmittedPattern(nextPattern);
    setCursor(0);
    setSelectedKey(null);
  };
  const onSearch = () => runSearch(pattern);
  const onToggleQuickFilter = (key: string) =>
    runSearch(key === submittedPattern ? DEFAULT_PATTERN : key);
  const onNext = () => {
    if (scan.data && scan.data.cursor !== 0) {
      setCursor(scan.data.cursor);
      setSelectedKey(null);
    }
  };
  const onDelete = (key: string) => {
    const confirmed = window.confirm(
      `Delete "${key}"?\n\n` +
        `This is a single-key DEL — the entry is gone, not re-routed, and the ` +
        `action cannot be undone.`
    );
    if (!confirmed) return;
    deleteMutation.mutate(key, {
      onSuccess: () => setSelectedKey(null)
    });
  };

  return (
    <>
      <PanelCard title={`Cache search — pattern: ${submittedPattern}`}>
        <QuickFilters selectedPattern={submittedPattern} onToggle={onToggleQuickFilter} />
        <SearchControls
          pattern={pattern}
          onPatternChange={setPattern}
          onSearch={onSearch}
          cursor={cursor}
          nextCursor={scan.data?.cursor ?? null}
          onNext={onNext}
          isFetching={scan.isFetching}
        />
        {scan.isLoading && !scan.data ? (
          <Loading />
        ) : scan.error ? (
          <ErrorDetail error={scan.error} />
        ) : (
          <KeyList
            keys={scan.data?.keys ?? []}
            selectedKey={selectedKey}
            onSelect={setSelectedKey}
          />
        )}
      </PanelCard>
      {selectedKey &&
        (inspect.isLoading ? (
          <Loading />
        ) : inspect.error && !isNotFound(inspect.error) ? (
          <PanelError title={`Selected: ${selectedKey}`} error={inspect.error} />
        ) : inspect.data ? (
          <InspectDetail selectedKey={selectedKey} inspect={inspect.data} onDelete={onDelete} />
        ) : (
          <PanelCard title={`Selected: ${selectedKey}`}>
            <p className="text-sm text-gray-500">Key not found (it may have just expired).</p>
          </PanelCard>
        ))}
    </>
  );
};

export default CachePanel;
